import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import AdminSidebar from './AdminSidebar';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { getShippingFees, updateShippingFees } from './services/firebaseService';

const fieldLabelClass = 'text-xs uppercase tracking-wider text-gray-500 font-semibold';
const fieldInputClass =
  'border border-gray-200 rounded p-2 bg-white text-primary focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-colors text-sm w-full font-mono';

const ManageShippingFees = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [fees, setFees] = useState({
    westMalaysia: { firstKg: 10, additionalKg: 3 },
    eastMalaysia: { firstKg: 10, additionalKg: 12 }
  });

  useEffect(() => {
    const loadShippingFees = async () => {
      setLoading(true);
      setError('');
      const result = await getShippingFees();
      if (result.success) {
        setFees(result.shippingFees);
      } else {
        setError(`Failed to load shipping fees: ${result.error}`);
      }
      setLoading(false);
    };

    loadShippingFees();
  }, []);

  const handleFieldChange = (region, field, value) => {
    setFees((prev) => ({
      ...prev,
      [region]: {
        ...prev[region],
        [field]: value
      }
    }));
  };

  const validate = () => {
    const values = [
      fees.westMalaysia.firstKg,
      fees.westMalaysia.additionalKg,
      fees.eastMalaysia.firstKg,
      fees.eastMalaysia.additionalKg
    ].map((v) => Number(v));

    if (values.some((v) => Number.isNaN(v) || v < 0)) {
      setError('All shipping fees must be valid numbers and cannot be negative.');
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!validate()) return;

    setSaving(true);
    setError('');
    const result = await updateShippingFees({
      westMalaysia: {
        firstKg: Number(fees.westMalaysia.firstKg),
        additionalKg: Number(fees.westMalaysia.additionalKg)
      },
      eastMalaysia: {
        firstKg: Number(fees.eastMalaysia.firstKg),
        additionalKg: Number(fees.eastMalaysia.additionalKg)
      }
    });

    if (result.success) {
      alert('Shipping fees updated successfully.');
      navigate('/admin-portal');
    } else {
      setError(`Failed to update shipping fees: ${result.error}`);
    }
    setSaving(false);
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header />
      <div className="pt-20 flex flex-1">
        <AdminSidebar activeItem="shippingFees" />
        <div className="flex-1 min-w-0 overflow-y-auto bg-gray-50 p-4 md:p-6">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-6 gap-4 border-b-2 border-gray-200 pb-4">
            <div>
              <button
                onClick={() => navigate('/admin-portal')}
                className="flex items-center gap-1 text-gray-500 hover:text-primary transition-colors mb-2 text-sm"
              >
                <ArrowBackIcon className="!w-[18px] !h-[18px]" />
                Back to Dashboard
              </button>
              <h1 className="text-3xl md:text-4xl font-bold text-gray-900">Shipping Fees</h1>
            </div>
          </div>

          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
              {error}
            </div>
          )}

          {loading ? (
            <div className="bg-white border border-gray-200 rounded shadow-sm p-8 text-center text-gray-500">
              Loading shipping fees...
            </div>
          ) : (
            <div className="space-y-6">
              <div className="bg-white p-4 border border-gray-200 rounded shadow-sm">
                <h3 className="text-lg font-bold text-primary mb-4 border-b border-gray-200 pb-1">
                  West Malaysia
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex flex-col gap-1">
                    <label className={fieldLabelClass} htmlFor="west_first_kg">
                      First 1kg (RM)
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-500 font-mono">
                        RM
                      </span>
                      <input
                        id="west_first_kg"
                        type="number"
                        min="0"
                        step="0.01"
                        value={fees.westMalaysia.firstKg}
                        onChange={(e) => handleFieldChange('westMalaysia', 'firstKg', e.target.value)}
                        className={`${fieldInputClass} pl-10`}
                      />
                    </div>
                  </div>
                  <div className="flex flex-col gap-1">
                    <label className={fieldLabelClass} htmlFor="west_additional_kg">
                      Additional 1kg (RM)
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-500 font-mono">
                        RM
                      </span>
                      <input
                        id="west_additional_kg"
                        type="number"
                        min="0"
                        step="0.01"
                        value={fees.westMalaysia.additionalKg}
                        onChange={(e) => handleFieldChange('westMalaysia', 'additionalKg', e.target.value)}
                        className={`${fieldInputClass} pl-10`}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white p-4 border border-gray-200 rounded shadow-sm">
                <h3 className="text-lg font-bold text-primary mb-4 border-b border-gray-200 pb-1">
                  East Malaysia (Sabah, Sarawak, Labuan)
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex flex-col gap-1">
                    <label className={fieldLabelClass} htmlFor="east_first_kg">
                      First 1kg (RM)
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-500 font-mono">
                        RM
                      </span>
                      <input
                        id="east_first_kg"
                        type="number"
                        min="0"
                        step="0.01"
                        value={fees.eastMalaysia.firstKg}
                        onChange={(e) => handleFieldChange('eastMalaysia', 'firstKg', e.target.value)}
                        className={`${fieldInputClass} pl-10`}
                      />
                    </div>
                  </div>
                  <div className="flex flex-col gap-1">
                    <label className={fieldLabelClass} htmlFor="east_additional_kg">
                      Additional 1kg (RM)
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-500 font-mono">
                        RM
                      </span>
                      <input
                        id="east_additional_kg"
                        type="number"
                        min="0"
                        step="0.01"
                        value={fees.eastMalaysia.additionalKg}
                        onChange={(e) => handleFieldChange('eastMalaysia', 'additionalKg', e.target.value)}
                        className={`${fieldInputClass} pl-10`}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-4 border-t-2 border-gray-200">
                <button
                  type="button"
                  onClick={() => navigate('/admin-portal')}
                  disabled={saving}
                  className="px-4 py-2 border-2 border-primary text-primary text-sm font-bold rounded hover:bg-gray-100 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="px-4 py-2 bg-primary text-white text-sm font-bold rounded hover:bg-blue-900 transition-colors shadow-sm disabled:opacity-50"
                >
                  {saving ? 'Saving...' : 'Save Shipping Fees'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default ManageShippingFees;
